import { Component } from "react";

const RELOAD_KEY = "invyt_chunk_reloaded_at";

// Matches the various "lazy chunk failed to load" errors across browsers/bundlers.
function isChunkError(error) {
  const msg = (error && (error.message || String(error))) || "";
  return /ChunkLoadError|Loading chunk [\d]+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload/i.test(msg);
}

// Wraps the lazy routes so a failed chunk (e.g. stale index.html after a
// redeploy, or a flaky connection) recovers with a single reload instead of
// sitting on the boot spinner forever. Persistent failures show a retry screen.
export default class ChunkErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    if (isChunkError(error)) {
      const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
      // Guard against reload loops: only auto-reload once per 10s window.
      if (Date.now() - last > 10000) {
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
        window.location.reload();
      }
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="min-h-screen flex items-center justify-center bg-slate-50 px-6"
          data-testid="chunk-error-fallback"
        >
          <div className="text-center max-w-sm">
            <h1 className="font-heading text-xl font-bold text-slate-900 mb-2">Having trouble loading</h1>
            <p className="text-slate-500 text-sm mb-6">
              A newer version may be available or your connection dropped. Reloading usually fixes it.
            </p>
            <button
              onClick={() => { sessionStorage.removeItem(RELOAD_KEY); window.location.reload(); }}
              className="h-11 px-6 rounded-xl bg-[#1E2019] text-white font-medium hover:bg-[#1E2019] transition-colors"
              data-testid="chunk-error-reload"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
